<template>
  <div class="flex flex-col h-full w-full min-h-0 bg-[#fafbfc] z-20">
    <div class="p-6 border-b border-[#1c4848]/5 bg-white shrink-0">
      <h2 class="text-xs font-bold uppercase tracking-wider text-[#1c4848]/60 mb-4">Podcast Management</h2>
      <button
        type="button"
        class="w-full py-2.5 bg-[#153838] hover:bg-[#1c4848] text-[#ecd79f] rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-[#1c4848]/10 hover:shadow-[#1c4848]/20 group"
        @click="showAddPodcastModal"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="group-hover:scale-110 transition-transform"><path d="M5 12h14"></path><path d="M12 5v14"></path></svg>
        Add Podcast
      </button>
    </div>

    <div class="flex-1 overflow-y-auto custom-scrollbar pt-4 pr-4 pb-4 pl-4 space-y-3 min-h-0">
      <div class="relative mb-2">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search podcasts..."
          class="w-full bg-white border border-[#1c4848]/10 rounded-lg px-3 py-2 text-xs font-medium focus:outline-none focus:border-[#1c4848]/30 pl-9"
        >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="absolute left-3 top-2.5 text-[#1c4848]/40 pointer-events-none"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>
      </div>

      <div
        v-for="podcast in filteredPodcasts"
        :key="podcast.id"
        role="button"
        tabindex="0"
        class="group flex items-center gap-3 p-3 rounded-xl border border-[#1c4848]/5 bg-white hover:border-[#1c4848]/20 hover:shadow-md transition-all cursor-pointer"
        @click="playPodcast(podcast)"
        @keydown.enter="playPodcast(podcast)"
        @keydown.space.prevent="playPodcast(podcast)"
      >
        <div class="w-10 h-10 rounded-full border border-[#1c4848]/10 overflow-hidden shrink-0 relative flex items-center justify-center bg-gray-50 group-hover:ring-2 group-hover:ring-[#1c4848]/20 transition-all">
          <img
            v-if="podcast.image_url"
            :src="podcast.image_url"
            :alt="podcast.title"
            class="w-full h-full object-cover pointer-events-none"
          >
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="text-[#1c4848]/40 pointer-events-none"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" x2="12" y1="19" y2="22"></line><line x1="8" x2="16" y1="22" y2="22"></line></svg>
          <div class="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" class="text-white ml-0.5"><polygon points="5 3 19 12 5 21 5 3"/></svg>
          </div>
        </div>
        <div class="flex-1 min-w-0 pointer-events-none">
          <h4 class="text-xs font-bold text-[#1c4848] truncate">{{ podcast.title }}</h4>
          <div class="flex items-center gap-1.5 mt-0.5">
            <span class="text-[10px] text-[#1c4848]/50 truncate">{{ podcast.author?.data?.nickname ?? podcast.author?.nickname ?? '—' }}</span>
          </div>
        </div>
        <div class="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" @click.stop>
          <button type="button" class="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors" title="Publish to feed" @click.stop.prevent="publishPodcast(podcast)">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5"/><path d="m5 12 7-7 7 7"/></svg>
          </button>
          <button type="button" class="p-1.5 text-blue-500 hover:bg-blue-50 rounded-md transition-colors" title="Edit" @click.stop.prevent="editPodcast(podcast)">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path></svg>
          </button>
          <button type="button" class="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Delete" @click.stop.prevent="deletePodcast(podcast.id)">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import AddPodcastModal from '@/components/app/dashboard/modals/AddPodcastModal.vue';
import { usePodcastStore } from '@/store/podcasts/index.js';
import { useModal } from 'vue-final-modal';
import { useContentFeedStore } from '@/store/content-feed/index.js';
import { usePlayerStore } from '@/store/player/index.js';
import { getContentFeedType, getContentLanguageId } from '@/utils/contentFeedType.js';

const searchQuery = ref('');
const podcastStore = usePodcastStore();
const contentFeedStore = useContentFeedStore();
const playerStore = usePlayerStore();

onMounted(() => {
  podcastStore.fetchPodcasts();
});

const podcasts = computed(() => podcastStore.podcasts);

const filteredPodcasts = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return podcasts.value;
  return podcasts.value.filter((p) => {
    const title = (p.title ?? '').toLowerCase();
    const author = (p.author?.data?.nickname ?? p.author?.nickname ?? '').toLowerCase();
    return title.includes(q) || author.includes(q);
  });
});

function showAddPodcastModal() {
  const { open, close } = useModal({
    component: AddPodcastModal,
    attrs: {
      data: {},
      onConfirm() {
        podcastStore.fetchPodcasts();
        close();
      }
    }
  });
  open();
}

function playPodcast(podcast) {
  const audioUrl = podcast.mp3_url ?? podcast.data?.mp3_url ?? '';
  playerStore.setTrack(
    {
      title: podcast.title ?? podcast.data?.title ?? '—',
      authorName: podcast.author?.data?.nickname ?? podcast.author?.nickname ?? podcast.data?.author?.nickname ?? '—',
      coverUrl: podcast.image_url ?? podcast.data?.image_url ?? '',
      audioUrl: audioUrl || undefined,
    },
    { autoplay: Boolean(audioUrl) }
  );
}

function editPodcast(podcast) {
  alert(`Edit functionality for "${podcast.title}" is not implemented yet.`);
}

function deletePodcast(podcastId) {
  if (confirm('Are you sure you want to delete this podcast?')) {
    podcastStore.deletePodcast(podcastId)
      .then(() => podcastStore.fetchPodcasts())
      .catch((err) => {
        console.error(err);
        alert('Failed to delete the podcast. Please try again.');
      });
  }
}

function publishPodcast(podcast) {
  if (!confirm('Are you sure you want to publish this podcast?')) return;
  const title = podcast?.title ?? 'Novi podcast';
  const type = getContentFeedType(podcast, 'podcast');
  const languageId = getContentLanguageId(podcast);
  contentFeedStore.createContentFeed({ title, type, language_id: languageId }).then((response) => {
    podcastStore.attachContentFeedToPodcast(podcast.id, response.id);
  });
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(28, 72, 72, 0.15);
  border-radius: 3px;
}
</style>
